import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Form,
  Typography,
  Radio,
  Divider,
  Select,
  Table,
  Input
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import useFilters from './Filters';
import useColumns from './Columns';

const AdvancedFilter = ({ setVisible, resourceName }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { getFilters } = useFilters();
  const { getColumns } = useColumns();
  const [searchFields, setSearchFields] = useState([]);
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState([]);
  const filtersRef = useRef();
  filtersRef.current = filters;

  const [removedSelect, setRemovedSelect] = useState([]);
  const removedSelectRef = useRef();
  removedSelectRef.current = removedSelect;

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [searchResult, setSearchResult] = useState();
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true
  });

  const [searchObject, setSearchObject] = useState({
    insurer: '',
    product: [],
    type: '',
    status: [],
    withAvenant: ''
  });

  const columns = getColumns(resourceName, setVisible);

  useEffect(() => {
    setSearchFields(getFilters(resourceName));
    // eslint-disable-next-line
  }, [resourceName]);

  const getObjects = async (_pagination, _searchObject) => {
    setLoading(true);
    const { pageSize, current } = _pagination;
    try {
      const { data } = await dispatchAPI('POST', {
        url: `/${resourceName}/advancedSearch`,
        body: {
          ..._searchObject,
          insurer: user?.insurer?._id,
          populate: 'customer,insurer',
          limit: pageSize,
          skip: (current - 1) * pageSize
        }
      });
      setPagination({
        ..._pagination,
        current,
        total: data[0].metadata.length === 0 ? 0 : data[0].metadata[0].total
      });
      setSearchResult(data[0].data);
      setTotal(data[0].metadata.length === 0 ? 0 : data[0].metadata[0].total);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setLoading(false);
  };

  const productChange = (value) => {
    const obj = searchObject;
    obj.product = value;
    setSearchObject(obj);
    getObjects({ ...pagination, current: 1 }, obj);
  };

  const typeChange = (e) => {
    const obj = searchObject;
    obj.type = e.target.value;
    setSearchObject(obj);
    getObjects({ ...pagination, current: 1 }, obj);
  };

  const statusChange = (value) => {
    const obj = searchObject;
    obj.status = value;
    setSearchObject(obj);
    getObjects({ ...pagination, current: 1 }, obj);
  };

  const avenantChange = (e) => {
    const obj = searchObject;
    obj.withAvenant = e.target.value;
    setSearchObject(obj);
    getObjects({ ...pagination, current: 1 }, obj);
  };

  const products = [
    { label: 'RCP', value: 'RCP' },
    { label: 'MRP', value: 'MRP' },
    { label: 'MRI', value: 'MRI' },
    { label: 'PREVOYANCE', value: 'PREVOYANCE' }
  ];

  const types = [
    { label: 'Contrat', value: 'contract' },
    { label: 'Devis', value: 'quote' },
    { label: 'Tous', value: '' }
  ];

  const avenants = [
    { label: 'Avec', value: true },
    { label: 'Sans', value: false },
    { label: 'Tous', value: '' }
  ];

  const status = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Impayé 1', value: 'UNPAID_1' },
    { label: 'Impayé 2', value: 'UNPAID_2' },
    { label: 'Impayé 3', value: 'UNPAID_3' },
    { label: 'Mise en demeure', value: 'FORMAL_NOTICE' },
    { label: 'En suspension impayés', value: 'WARRANTY_SUSPENSION' }
  ];

  const getChildren = (items) => {
    const children = [];
    for (let i = 0; i < items.length; i += 1) {
      children.push(
        <Select.Option key={i.toString(36) + i} value={items[i].value}>
          {items[i].label}
        </Select.Option>
      );
    }
    return children;
  };

  const handlePageChange = (page) => {
    setPagination(page);
    getObjects(page, searchObject);
  };

  const onFinish = (values) => {
    const obj = searchObject;
    obj.filters = Object.values(values);
    setSearchObject(obj);
    getObjects({ ...pagination, current: 1 }, obj);
  };

  const addNewFilter = (newFilter) => {
    if (!selectedFilter) return;
    const filter = searchFields.find((item) => item.searchField === newFilter);
    setRemovedSelect([...removedSelect, newFilter]);

    const index = filters.length;
    form.setFieldsValue({ [index]: { filter: newFilter } });
    form.setFieldsValue({ [index]: { type: filter.type } });
    const filterUI = (
      <div
        key={index}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
          justifyContent: 'center'
        }}
      >
        <Form.Item name={[index, 'filter']} style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name={[index, 'type']} style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        <Form.Item name={[index, 'hidden']}>
          <Input
            defaultValue={t(`advancedSearch.searchFields.${filter.filter}`)}
            disabled
          />
        </Form.Item>
        {filter.regexOptions && (
          <Form.Item
            name={[index, 'regex']}
            rules={[{ required: true }]}
            style={{ width: '30%', margin: '0 5px' }}
            initialValue={filter.regexOptions[0]}
          >
            <Select
              options={filter.regexOptions.map((opt) => ({
                value: opt,
                label: t(`advancedSearch.searchFields.regex.${opt}`)
              }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name={[index, 'value']}
          rules={[{ required: true }]}
          style={{
            width: filter.regexOptions ? '50%' : '80%',
            margin: '0 5px'
          }}
        >
          {filter.render}
        </Form.Item>
        <Button
          type="danger"
          className="dynamic-delete-button"
          onClick={() => {
            const obj = searchObject;
            if (obj.filters) {
              obj.filters = obj.filters.filter(
                (item) => item.filter !== newFilter
              );
              setSearchObject(obj);
              getObjects({ ...pagination, current: 1 }, obj);
            }
            const newFilters = filtersRef.current.filter(
              (item) => item.filter !== newFilter
            );
            setFilters(newFilters);
            const d = removedSelectRef.current.filter(
              (item) => item !== newFilter
            );
            setRemovedSelect(d);
          }}
          icon={<MinusCircleOutlined />}
          style={{ height: 40 }}
        >
          Annuler
        </Button>
      </div>
    );
    setSelectedFilter(null);
    setFilters([...filters, { filter: newFilter, ui: filterUI }]);
  };

  return (
    <div>
      {resourceName === 'contracts' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            marginBottom: 15
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              flexWrap: 'wrap',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ marginBottom: 20, width: '46%' }}>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                {t('advancedSearch.contractType')}
              </Typography.Paragraph>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder={t('advancedSearch.contractType')}
                onChange={productChange}
              >
                {getChildren(products)}
              </Select>
            </div>
            <div style={{ marginBottom: 20, width: '46%' }}>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                {t('advancedSearch.status')}
              </Typography.Paragraph>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder={t('advancedSearch.status')}
                onChange={statusChange}
              >
                {getChildren(status)}
              </Select>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              flexWrap: 'wrap',
              justifyContent: 'flex-start'
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <Typography.Paragraph style={{ marginBottom: 6 }}>
                {t('advancedSearch.type')}
              </Typography.Paragraph>
              <Radio.Group
                style={{ width: 405 }}
                options={types}
                onChange={typeChange}
                optionType="button"
                defaultValue=""
                buttonStyle="solid"
              />
            </div>
            <div>
              <Typography.Paragraph style={{ marginBottom: 6 }}>
                {t('advancedSearch.avenant')}
              </Typography.Paragraph>
              <Radio.Group
                style={{ width: 330 }}
                options={avenants}
                onChange={avenantChange}
                optionType="button"
                defaultValue=""
                buttonStyle="solid"
              />
            </div>
          </div>
        </div>
      )}
      <Divider orientation="left" plain>
        <Typography.Text>{t('advancedSearch.mysearch')}</Typography.Text>
      </Divider>
      <Form name="dynamic_form_item" onFinish={onFinish} form={form}>
        <div>
          {filters.map((filter) => {
            return filter.ui;
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Form.Item>
              <Select
                allowClear
                style={{ width: 150, marginRight: 7 }}
                placeholder={t('placeholder.search')}
                filterOption={false}
                value={selectedFilter}
                onSelect={(v) => {
                  setSelectedFilter(v);
                }}
              >
                {searchFields.map((item, key) => {
                  const d = removedSelect.find(
                    (_item) => _item === item.searchField
                  );
                  if (d === undefined) {
                    return (
                      <Select.Option
                        key={key.toString(36)}
                        value={item.searchField}
                      >
                        {t(`advancedSearch.searchFields.${item.filter}`)}
                      </Select.Option>
                    );
                  }
                  return null;
                })}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => addNewFilter(selectedFilter)}
                icon={<PlusOutlined />}
                style={{ height: '40px' }}
              >
                Ajouter un filtre
              </Button>
            </Form.Item>
          </div>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 0 }}
        >
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Chercher
            </Button>
          </Form.Item>
        </div>
      </Form>
      <Divider orientation="left" plain>
        <Typography.Text>
          {`${t('advancedSearch.result')} ${total > 0 ? `(${total})` : ''}`}
        </Typography.Text>
      </Divider>
      <Table
        style={{ cursor: 'pointer' }}
        columns={columns}
        dataSource={searchResult && searchResult}
        pagination={pagination}
        loading={loading}
        onChange={handlePageChange}
        scroll={{ y: 200 }}
      />
    </div>
  );
};

AdvancedFilter.propTypes = {
  setVisible: PropTypes.func.isRequired,
  resourceName: PropTypes.string.isRequired
};

export default AdvancedFilter;
